<template>
  <BContainer
    class="mt-2"
    fluid
  >
    <!-- Filters -->
    <BRow>
      <BCol
        cols="12"
        md="8"
        class="px-1"
      >
        <BFormGroup
          label="Search"
          label-for="filterHistorySearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistorySearch"
            v-model="tableHistories.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="6"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Posted From"
          label-for="filterHistoryDateFrom"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistoryDateFrom"
            v-model="tableHistories.filter.date_from"
            :max="tableHistories.filter.date_to"
            debounce="1000"
            type="date"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="6"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Posted To"
          label-for="filterHistoryDateTo"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistoryDateTo"
            v-model="tableHistories.filter.date_to"
            :min="tableHistories.filter.date_from"
            debounce="1000"
            type="date"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <!-- Table -->
    <BRow
      v-if="Number(tabIndex) === 2"
      class="mt-1"
    >
      <BCol
        cols="12"
        class="px-1"
      >
        <BTable
          ref="tableHistories"
          small
          hover
          striped
          bordered
          responsive
          show-empty
          :items="tableProvider"
          :busy="tableHistories.busy"
          :filter="tableHistories.filter"
          :fields="tableHistories.fields"
          :stacked="tableHistories.stacked"
          :sort-by.sync="tableHistories.sortBy"
          :sort-desc.sync="tableHistories.sortDesc"
          :sort-direction="tableHistories.sortDirection"
          :filter-included-fields="tableHistories.filterOn"
          :current-page="tableHistories.currentPage"
          :per-page="tableHistories.perPage"
        >
          <template #cell(index)="{ index }">
            {{ tableRowNumber(tableHistories, index) }}
          </template>

          <template #row-details="row">
            <div class="p-2 bg-white">
              <BTable
                small
                hover
                bordered
                responsive
                show-empty
                class="col-md-6 col-12"
                :stacked="tableHistories.stacked"
                :items="row.item.approvable.transfer_details"
                :fields="[
                  { key: 'index', label: '#', class: 'text-center' },
                  { key: 'product_name', label: 'Product Name', class: 'col-md-6 col-12' },
                  { key: 'quantity', label: 'Ordered Quantity', class: 'text-center min-w-100px' },
                  { key: 'approved_quantity', label: 'Approved Quantity', class: 'text-center min-w-100px' },
                  { key: 'posting_quantity', label: 'Posted Quantity', class: 'text-center min-w-100px' }
                ]"
              >
                <template #cell(index)="{ index }">
                  <div style="min-width: 25px;">
                    {{ tableRowNumber(tableHistories, index) }}
                  </div>
                </template>

                <template #cell(quantity)="{ value }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      class="font-weight-bolder"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>

                <template #cell(approved_quantity)="{ value }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      class="font-weight-bolder"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>

                <template #cell(posting_quantity)="{ value, item }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      :class="['font-weight-bolder', {
                        'text-danger': Number(value) <= 0,
                        'text-warning': Number(value) >= 1 && Number(item.approved_quantity) !== Number(value),
                        'text-success': Number(item.approved_quantity) === Number(value)
                      }]"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>
              </BTable>
            </div>
          </template>

          <template #cell(action)="{ item, toggleDetails, detailsShowing }">
            <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
              <BButton
                size="sm"
                variant="primary"
                class="mr-0 mr-md-1 mb-1 mb-md-0"
                :disabled="tableHistories.busy"
                @click.prevent="show(item)"
              >
                View
              </BButton>
              <BButton
                size="sm"
                :variant="detailsShowing ? 'danger' : 'warning' "
                :disabled="tableHistories.busy"
                @click.prevent="toggleDetails"
              >
                {{ detailsShowing ? 'Hide' : 'Show' }} Products
              </BButton>
            </div>
          </template>

          <template #cell(approvable.transaction_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(approvable.document_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(approvable.sap_doc_entry)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(approvable.transfer_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(approvable.status)="{ value }">
            <b
              :class="[
                'min-w-80px',
                statusByVariant(value, 'text')
              ]"
              v-text="value"
            />
          </template>

          <template #cell(status)="{ value }">
            <b
              :class="[
                'min-w-80px',
                statusByVariant(value, 'text')
              ]"
              v-text="value"
            />
          </template>

          <template #cell()="{ value }">
            <div
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #table-busy>
            <div class="text-center py-5">
              <BIcon
                icon="stopwatch"
                font-scale="5"
                animation="cylon"
              />
              <p class="h3 py-2">
                <strong>Loading . . .</strong>
              </p>
            </div>
          </template>

        </BTable>
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-2 px-1"
      >
        <BFormSelect
          v-model="tableHistories.perPage"
          :options="tableHistories.pageOptions"
          :disabled="tableHistories.busy"
          class="w-100 w-md-25"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
      >
        <BPagination
          v-model="tableHistories.currentPage"
          :total-rows="tableHistories.totalRows"
          :per-page="tableHistories.perPage"
          :disabled="tableHistories.busy"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>

    </BRow>
  </BContainer>
</template>
<script>
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import { SWarehousePOPosting } from '@/services/user/wo'

export default {

  name: 'VWarehousePostingHistory',

  mixins: [
    MISC,
    FORMATTER
  ],

  props: {
    tabIndex: {
      type: Number,
      default: () => 0
    }
  },

  data () {
    return {
      tableHistories: {
        busy: false,
        filter: {
          search: '',
          date_from: '',
          date_to: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 14, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'approvable.created_at', label: 'Ordered At', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'approvable.status', label: 'Order Status', class: 'text-center' },
          { mobile: 3, key: 'approvable.transaction_number', label: 'Transaction Number', class: 'text-center min-w-100px', sortable: true },
          { mobile: 4, key: 'approvable.document_number', label: 'Document Number', class: 'text-center min-w-100px', sortable: true },
          { mobile: 5, key: 'approvable.transfer_number', label: 'Transfer Number', class: 'text-center min-w-100px', sortable: true },
          { mobile: 6, key: 'approvable.sap_doc_entry', label: 'SAP Entry', class: 'text-center min-w-100px', sortable: true },
          { mobile: 7, key: 'approvable.destination_name', label: 'Ordered By', class: 'min-w-200px' },
          { mobile: 8, key: 'approvable.group_name', label: 'Group', class: 'min-w-200px' },
          { mobile: 9, key: 'approvable.transfer_details.length', label: 'Products', class: 'text-center' },
          { mobile: 10, key: 'approvable.delivery_details.customer', label: 'Customer', class: 'min-w-200px' },
          { mobile: 11, key: 'approvable.customer_direct', label: 'D2C', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 12, key: 'status', label: 'Approval Status', class: 'text-center' },
          { mobile: 13, key: 'status_at', label: 'Posted At', formatter: this.dateTimeShortFormatter }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableHistories.busy = true
      return await SWarehousePOPosting.histories({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        filter_date_from: filter.date_from,
        filter_date_to: filter.date_to
      }).then(({ data }) => {
        this.tableHistories.totalRows = data.total_rows
        return data.items
      }).catch(() => {
        this.tableHistories.totalRows = 0
        return []
      }).finally(() => {
        this.tableHistories.busy = false
      })
    },

    tableRefresh () {
      this.$refs.tableHistories.refresh()
    },

    show (stockRequest) {
      this.$emit('show', stockRequest, false)
    }
  }
}
</script>
